import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ContactIcon } from "@/components/icons";
import { fileToBase64 } from "@/utils/conversion";
import { useCreateComplaint } from "../repositories/complaints-repository";
import { useGetLicences } from "@/features/applications/repositories/licences-repository";

import Button from "@/components/buttons/Button";
import SelectInput from "@/components/form-controls/SelectInput";
import Modal from "@/components/modal";
import RichTextEditor from "@/components/form-controls/RichTextEditor";
import FileInput from "@/components/form-controls/FileInput";
import toast from "react-hot-toast";

interface IProps {
    isOpen: boolean;
    onClose: (value: boolean) => void;
    editData?: ShareholderFormData | null;
}

export interface ShareholderFormData {
    id: string;
    complaintType: string;
    complaintDescription: string;
    complaintPhoto: string;
    /** Licence application the complaint concerns. Optional — a complaint may be general. */
    licenseId: string;
}

export default function ComplaintsModal({ isOpen, onClose, editData, }: IProps) {
    const methods = useForm<ShareholderFormData>({
        defaultValues: {
            id: "",
            complaintType: "",
            complaintDescription: "",
            complaintPhoto: "",
            licenseId: "",
        },
    });

    const { register, setValue, reset, formState: { errors } } = methods;

    const {mutate,isPending} = useCreateComplaint()

    // The applicant's own licence applications, so the complaint can be tied to the
    // one it is about. Same endpoint the "My applications" table reads, so the list
    // is exactly what this user can already see. licenseId stays OPTIONAL: a general
    // complaint has no licence, and a wrong id is worse than none.
    const { licences, isLoading: isLoadingLicences } = useGetLicences({
        pager: { page: 1, size: 100 }
    })

    const licenceOptions = (licences?.results ?? [])
        .filter((licence: any) => licence?.id !== null && licence?.id !== undefined)
        .map((licence: any) => ({
            value: String(licence.id),
            label: licence.projectName
                ? `${licence.projectName} (#${licence.id})`
                : `Application #${licence.id}`,
        }))

    useEffect(() => {
        if (editData) {
            Object.entries(editData).forEach(([key, value]) => {
                setValue(key as keyof ShareholderFormData, value);
            });
        } else {
            reset(); 
        }
    }, [editData, setValue, reset]);

    const onFileChange = async (value: FileList) => {
        const base64String = await fileToBase64(value[0]) as string
        setValue("complaintPhoto", base64String);
    }
    
    const handleAddOrUpdate = (data: ShareholderFormData) => {    
        const mappedData = {
            type: data.complaintType,
            subject: data.complaintDescription,
            description: data.complaintDescription,
            attachments: [data.complaintPhoto],
            // Backend ComplaintCreateRequest.licenseId is a Long; omit it entirely
            // when the applicant did not pick a licence.
            ...(data.licenseId ? { licenseId: Number(data.licenseId) } : {}),
        };

        mutate(mappedData, {
            onSuccess: () => {
                onClose(false);
                reset();
            },
            onError: (error) => {
                toast.error("Failed to submit the complaint. Please try again.");
                console.error(error);
            },
        });
    };
    


    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <div className="absolute top-4 left-6 border border-[#E4E7EC] rounded-lg p-2.5">
                <ContactIcon/>
            </div>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleAddOrUpdate)}>
                    <div className="grid grid-cols-2 gap-4 w-full p-1 mt-2">
                        <div className="col-span-2">
                            <h1 className="text-[#101828] text-lg">
                                {editData ? "Edit" : "Send"}  Complaint
                            </h1>
                            <span className="text-[#475467] text-xs">
                                {editData ? "Modify" : "Send"}  new complaint to our management team
                            </span>
                        </div>

                        <SelectInput
                            label="Complaint Type"
                            displayName="label"
                            valueName="value"
                            error={errors.complaintType?.message?.toString()}
                            register={register("complaintType", { required: "Complaint Type is required" })}
                            options={[
                                { label: "Problem ", value: "PROBLEM" },
                                { label: "Suggestion", value: "SUGGESTION" },
                                { label: "Feedback", value: "FEEDBACK" },
                                { label: "Service Related", value: "SERVICE_RELATED" },
                                { label: "Technical Issue", value: "TECHNICAL_ISSUE" },
                                { label: "Question", value: "INQUIRY" },
                                { label: "Other", value: "OTHER" },
                            ]}
                            required
                            className="col-span-2"
                        />

                        <SelectInput
                            label="Related Application"
                            displayName="label"
                            valueName="value"
                            hint="Optional"
                            placeholder={
                                isLoadingLicences
                                    ? "Loading your applications..."
                                    : licenceOptions.length === 0
                                        ? "You have no licence applications yet"
                                        : "Select the application this is about"
                            }
                            disabled={isLoadingLicences || licenceOptions.length === 0}
                            error={errors.licenseId?.message?.toString()}
                            register={register("licenseId")}
                            options={licenceOptions}
                            className="col-span-2"
                        />

                        <RichTextEditor
                            name="complaintDescription"                       
                            placeholder="Type here..."
                            label="Complaint Subject "
                            className="col-span-2"
                            register={register("complaintDescription", { required: "Complaint Description is required" })}
                        />

                        <FileInput
                            name="complaintPhoto"
                            label="Attach File"
                            className="col-span-2"
                            placeholder={""}
                            onChange={onFileChange}
                        />
                    

                        <div className="col-span-2">
                            <div className="flex justify-between items-center w-full gap-3">
                                <Button type="button" className="w-full" size="md" intent="default" onClick={() => onClose(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    loading={isPending}
                                    className="w-full"
                                    size="md"
                                    intent="primary"
                                    type="submit"
                                >
                                Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>    
            </FormProvider>
        </Modal>
    );
}
